import {
  Banknote,
  BedDouble,
  Building2,
  CalendarCheck,
  CalendarDays,
  Gauge,
  MapPin,
} from "lucide-react";
import { useMemo, useState } from "react";
import benchmarks from "../data/rentalBenchmarks.json";

const nok = new Intl.NumberFormat("nb-NO", {
  style: "currency",
  currency: "NOK",
  maximumFractionDigits: 0,
});

const money = (value) => (value == null ? "Ikke publisert" : nok.format(value));
const roundTo = (value, nearest = 500) => nearest * Math.round(value / nearest);

const top25Breakdown = (row) => {
  const gross = row.grossPerPropertyP75;
  const platform = roundTo(gross * 0.16);
  const heimby = roundTo(gross * 0.15 * 1.25);
  const cleaning = roundTo(gross * 0.15);
  return {
    gross,
    platform,
    heimby,
    cleaning,
    owner: gross - platform - heimby - cleaning,
  };
};

const dimensions = [
  { key: "city", label: "By og marked", icon: MapPin },
  { key: "bedrooms", label: "Antall soverom", icon: BedDouble },
];

const SummaryCard = ({ icon: Icon, label, value, note, dark = false }) => (
  <div
    className={`rounded-2xl border p-5 md:p-6 ${
      dark
        ? "border-gray-900 bg-gray-900 text-white"
        : "border-gray-200 bg-white text-gray-900"
    }`}
  >
    <div className="mb-5 flex items-center gap-2">
      <Icon className={`h-5 w-5 ${dark ? "text-white/65" : "text-gray-500"}`} aria-hidden="true" />
      <span className={`text-sm font-semibold ${dark ? "text-white/65" : "text-gray-600"}`}>
        {label}
      </span>
    </div>
    <p className="text-3xl font-bold tracking-tight md:text-4xl">{value}</p>
    <p className={`mt-3 text-sm leading-relaxed ${dark ? "text-white/60" : "text-gray-500"}`}>
      {note}
    </p>
  </div>
);

const RentalBenchmarkCalculator = () => {
  const [year, setYear] = useState(benchmarks.years[0]);
  const [month, setMonth] = useState(8);
  const [dimension, setDimension] = useState("city");

  const bergen = benchmarks.groups.city.find(
    (row) => row.key === "Bergen" && row.year === year && row.month === month,
  );
  const rows = useMemo(
    () => benchmarks.groups[dimension].filter(
      (row) => row.year === year && row.month === month,
    ),
    [dimension, month, year],
  );

  if (!bergen) return null;

  const selectedMonth = benchmarks.months.find((item) => item.value === month);
  const visibleRows = dimension === "city"
    ? rows.filter((row) => row.key !== "Bergen")
    : rows;
  const bergenTop25 = top25Breakdown(bergen);

  return (
    <section id="inntektskalkulator" className="bg-[#F9F8F4] px-6 py-16 md:py-20">
      <div className="mx-auto max-w-6xl">
        <div className="max-w-4xl">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-gray-700">
            <CalendarCheck className="h-3.5 w-3.5" aria-hidden="true" />
            {benchmarks.title}
          </div>
          <h2 className="text-3xl font-bold leading-tight text-gray-900 md:text-5xl">
            Faktisk sommeromsetning – bare for døgn boligen kunne selges
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-gray-700">
            Vi har kontrollert annonsestatus og kalenderen dag for dag. Boliger som
            startet sent eller bare var åpne i en ferieperiode får derfor ikke resten
            av måneden registrert som tomgang.
          </p>
        </div>

        <div className="mt-8 flex flex-wrap gap-2" aria-label="Velg år">
          {benchmarks.years.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setYear(item)}
              aria-pressed={year === item}
              className={`min-h-11 rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${
                year === item
                  ? "bg-gray-900 text-white"
                  : "border border-gray-300 bg-white text-gray-700 hover:border-gray-900"
              }`}
            >
              {item}
            </button>
          ))}
        </div>

        <div className="mb-8 mt-3 flex flex-wrap gap-2" aria-label="Velg måned">
          {benchmarks.months.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => setMonth(item.value)}
              aria-pressed={month === item.value}
              className={`min-h-11 rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${
                month === item.value
                  ? "bg-gray-900 text-white"
                  : "border border-gray-300 bg-white text-gray-700 hover:border-gray-900"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        {year === 2026 && month === 8 && (
          <p className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-relaxed text-amber-950">
            August viser bekreftede bookinger og åpne kalenderdøgn per 28. august,
            inkludert de siste dagene i måneden. Tallet er derfor en oppdatert bookingstatus,
            ikke et endelig månedsoppgjør.
          </p>
        )}

        <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-5 py-4">
          <p className="font-semibold text-gray-900">
            Bergen som markedseksempel
          </p>
          <p className="text-sm text-gray-500">{selectedMonth.label} {year}</p>
        </div>

        <p className="mb-5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm leading-relaxed text-emerald-950">
          Bergen er markedet som presterer best i dette utvalget. Belegget beregnes bare
          av døgn boligen faktisk kunne selges; tid før annonsen åpnet, eierblokker og
          andre stengte døgn registreres ikke som tomgang.
        </p>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <SummaryCard
            icon={Banknote}
            label="Brutto · topp 25 %"
            value={money(bergenTop25.gross)}
            note="Terskelen for den best presterende fjerdedelen av Bergen-boligene."
            dark
          />
          <SummaryCard
            icon={Building2}
            label="Til eier · topp 25 %"
            value={money(bergenTop25.owner)}
            note="Etter plattform 16 %, Heimby 15 % + MVA og ca. 15 % renhold."
          />
          <SummaryCard
            icon={Gauge}
            label="Belegg av salgbare døgn"
            value={`${String(bergen.occupancyPct).replace(".", ",")} %`}
            note="Bookede Airbnb- og Booking.com-døgn delt på døgn boligene faktisk var åpne for salg."
          />
          <SummaryCard
            icon={CalendarDays}
            label="ADR"
            value={money(bergen.adr)}
            note="Vektet losjiinntekt per Airbnb- og Booking.com-natt."
          />
        </div>

        <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5 md:p-6">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-gray-500">
                Topp 25 % i Bergen
              </p>
              <h3 className="mt-1 text-xl font-bold text-gray-900">
                Fra bruttoinntekt til beregnet eierinntekt
              </h3>
            </div>
            <p className="text-sm text-gray-500">Terskel · {selectedMonth.label} {year}</p>
          </div>
          <dl className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {[
              ["Bruttoinntekt", bergenTop25.gross, false],
              ["Plattform 16 %", bergenTop25.platform, true],
              ["Heimby 15 % + MVA", bergenTop25.heimby, true],
              ["Renhold ca. 15 %", bergenTop25.cleaning, true],
              ["Beregnet til eier", bergenTop25.owner, false],
            ].map(([label, value, deduction], index) => (
              <div
                key={label}
                className={`rounded-xl px-4 py-4 ${index === 4 ? "bg-emerald-50" : "bg-gray-50"}`}
              >
                <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</dt>
                <dd className={`mt-2 text-xl font-bold ${index === 4 ? "text-emerald-900" : "text-gray-900"}`}>
                  {deduction ? "−" : ""}{money(value)}
                </dd>
              </div>
            ))}
          </dl>
          <p className="mt-4 text-sm leading-relaxed text-gray-500">
            Plattformkommisjonen er standardisert til 16 prosent for sammenligning.
            Heimby-honoraret er 15 prosent pluss MVA, og renhold er estimert til
            omtrent 15 prosent.
            Eierbeløpet er før skatt, vedlikehold, skader, refusjoner og andre individuelle kostnader.
          </p>
        </div>

        <div className="mt-14">
          <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-gray-500">
                {dimension === "city" ? "Sammenlign andre byer" : "Sammenlign boligstørrelser"}
              </p>
              <h3 className="mt-2 text-2xl font-bold text-gray-900 md:text-3xl">
                {dimension === "city" ? "Andre byer sammenlignet med Bergen" : "Statistikk per antall soverom"}
              </h3>
            </div>
            <div className="flex flex-wrap gap-2" aria-label="Velg gruppering">
              {dimensions.map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setDimension(key)}
                  aria-pressed={dimension === key}
                  className={`inline-flex min-h-11 items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors ${
                    dimension === key
                      ? "bg-gray-900 text-white"
                      : "border border-gray-300 bg-white text-gray-700 hover:border-gray-900"
                  }`}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" /> {label}
                </button>
              ))}
            </div>
          </div>

          {dimension === "city" && (
            <p className="mt-4 text-sm leading-relaxed text-gray-600">
              {visibleRows.length > 0
                ? "Bergen-eksemplet vises over. Tabellen viser de øvrige markedene for samme måned."
                : "Bergen-eksemplet vises over. Andre bygrupper skjules denne måneden fordi de ikke oppfyller anonymiseringskravet."}
            </p>
          )}

          {visibleRows.length > 0 && (
          <div className="mt-6 overflow-x-auto rounded-2xl border border-gray-200 bg-white">
            <table className="min-w-[1180px] w-full text-left text-sm">
              <thead className="border-b border-gray-200 bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-4 py-3 font-bold">Gruppe</th>
                  <th className="px-4 py-3 font-bold">Belegg</th>
                  <th className="px-4 py-3 font-bold">ADR</th>
                  <th className="px-4 py-3 font-bold">Brutto · topp 25 %</th>
                  <th className="px-4 py-3 font-bold">Plattform 16 %</th>
                  <th className="px-4 py-3 font-bold">Heimby 15 % + MVA</th>
                  <th className="px-4 py-3 font-bold">Renhold ca. 15 %</th>
                  <th className="px-4 py-3 font-bold">Til eier · topp 25 %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visibleRows.map((row) => {
                  const top25 = top25Breakdown(row);
                  return (
                  <tr key={`${row.key}-${row.year}-${row.month}`}>
                    <th scope="row" className="whitespace-nowrap px-4 py-4 font-bold text-gray-900">
                      {row.label}
                    </th>
                    <td className="px-4 py-4 font-semibold text-gray-900">
                      {String(row.occupancyPct).replace(".", ",")} %
                    </td>
                    <td className="px-4 py-4 text-gray-700">{money(row.adr)}</td>
                    <td className="px-4 py-4 font-semibold text-gray-900">{money(top25.gross)}</td>
                    <td className="px-4 py-4 text-gray-700">{money(top25.platform)}</td>
                    <td className="px-4 py-4 text-gray-700">{money(top25.heimby)}</td>
                    <td className="px-4 py-4 text-gray-700">{money(top25.cleaning)}</td>
                    <td className="px-4 py-4 font-semibold text-gray-900">{money(top25.owner)}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        </div>

        <div className="mt-8 rounded-xl border border-gray-200 bg-white/70 p-5 text-sm leading-relaxed text-gray-600">
          <p className="font-semibold text-gray-900">Hva som er med – og hva som er trukket fra</p>
          <p className="mt-2">{benchmarks.method.basis}</p>
          <p className="mt-2">{benchmarks.method.availability}</p>
          <p className="mt-2">{benchmarks.method.platformCommission}</p>
          <p className="mt-2">{benchmarks.method.heimbyCommission}</p>
          <p className="mt-2">{benchmarks.method.cleaningCost}</p>
          <p className="mt-2">{benchmarks.method.ownerIncome}</p>
          <p className="mt-2">{benchmarks.privacy.groupRule} {benchmarks.privacy.rounding}</p>
          <p className="mt-3 text-xs text-gray-500">
            Kilde: live Guesty-kalender og anonymiserte reservasjons-, kostnads- og
            markedstall i Proptonomy. Oppdatert 28. august 2026.
          </p>
        </div>
      </div>
    </section>
  );
};

export default RentalBenchmarkCalculator;
